import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { getMIPInvestorJourneysData } from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';
import { mapJourneysToCards } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/ActiveJourneysCard/activeJourneys.utils.ts';
import { ActiveJourney } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/ActiveJourneysCard/activeJourneys.types.ts';

interface Props {
	dealId: string;
	mipId: string;
	investorId: string;
}

interface Result {
	journeys: ActiveJourney[];
	isLoading: boolean;
	isError: boolean;
}

export const useInvestorJourneys = ({ dealId, mipId, investorId }: Props): Result => {
	const tMipManage = useTranslationMipManage();
	const { value: canOpenInvestmentDetails } = useDealstackFeatureFlag(
		FEATURE_FLAG.INVESTMENT_DETAILS
	);

	const journeysQuery = useQuery({
		queryKey: [API_PATHS.MIP_INVESTOR_JOURNEYS_ID_GET, { investorId }],
		queryFn: ({ signal }) =>
			getMIPInvestorJourneysData(
				API_PATHS.MIP_INVESTOR_JOURNEYS_ID_GET,
				{ investorId },
				signal
			),
	});

	const journeys = useMemo(
		() =>
			mapJourneysToCards({
				journeys: journeysQuery.data?.payload?.journeys,
				dealId,
				mipId,
				investorId,
				canOpenInvestmentDetails: !!canOpenInvestmentDetails,
				tMipManage,
			}),
		[journeysQuery.data, dealId, mipId, investorId, canOpenInvestmentDetails, tMipManage]
	);

	return {
		journeys,
		isLoading: journeysQuery.isPending,
		isError: journeysQuery.isError,
	};
};
